import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Search, Plus, Upload, Download, FileUp, MoveRight, Trash2, X,
  RefreshCw, Target,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';

import { LEAD_STAGES } from '../components/leads/constants';
import { PipelineColumn } from '../components/leads/PipelineColumn';
import { LeadDetailModal } from '../components/leads/LeadDetailModal';
import { AddLeadModal } from '../components/leads/AddLeadModal';
import { CSVImportModal } from '../components/leads/CSVImportModal';

/**
 * LeadsPipelinePage — the top-level page that stitches the extracted
 * board/modal components together. Owns the async data flow (fetch,
 * mutation, refresh) and selection state.
 *
 * Refactored in iter119 (Feb 2026): originally 1470 lines; the modals,
 * column, and card components moved to /components/leads/*. See CHANGELOG.
 */
const LeadsPipelinePage = () => {
  const { chatbotId } = useParams();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chatbots, setChatbots] = useState([]);
  const [selectedChatbot, setSelectedChatbot] = useState(chatbotId || '');
  const [selectedLead, setSelectedLead] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [pipelineStats, setPipelineStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [draggingOver, setDraggingOver] = useState(null);
  const [chatbotsLoaded, setChatbotsLoaded] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState(new Set());
  const [bulkActionsOpen, setBulkActionsOpen] = useState(false);

  // ── Load chatbot list once ────────────────────────────────────────────
  useEffect(() => {
    const loadChatbots = async () => {
      try {
        const response = await api.get('/chatbots');
        const data = Array.isArray(response.data)
          ? response.data
          : response.data.chatbots || [];
        setChatbots(data);
        if (!selectedChatbot && data.length > 0) {
          setSelectedChatbot(data[0].chatbot_id);
        }
        setChatbotsLoaded(true);
      } catch (error) {
        console.error('Error loading chatbots:', error);
        setChatbotsLoaded(true);
        setLoading(false);
      }
    };
    loadChatbots();
  }, []);

  // ── Load leads + stats whenever the selected chatbot changes ──────────
  const loadLeads = useCallback(async () => {
    if (!selectedChatbot || !chatbotsLoaded) {
      if (chatbotsLoaded && !selectedChatbot) setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [leadsRes, statsRes] = await Promise.all([
        api.get(`/leads/chatbot/${selectedChatbot}?limit=200`),
        api.get(`/leads/chatbot/${selectedChatbot}/pipeline-stats`),
      ]);
      setLeads(leadsRes.data.leads || []);
      setPipelineStats(statsRes.data);
    } catch (error) {
      console.error('Error loading leads:', error);
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [selectedChatbot, chatbotsLoaded]);

  useEffect(() => { loadLeads(); }, [loadLeads]);

  // ── Drag-drop stage change from the board ─────────────────────────────
  const handleDrop = async (e, newStage) => {
    e.preventDefault();
    const leadId = e.dataTransfer.getData('leadId');
    setDraggingOver(null);
    if (!leadId) return;
    try {
      await api.put(`/leads/${leadId}/stage`, { stage: newStage });
      setLeads((prev) => prev.map((l) => (l.lead_id === leadId ? { ...l, stage: newStage } : l)));
      toast.success(`Lead moved to ${newStage} 🎯`);
    } catch (error) {
      toast.error('Failed to move lead');
    }
  };

  // ── Modal callbacks ───────────────────────────────────────────────────
  const handleUpdateLead = async (leadId, data) => {
    try {
      await api.put(`/leads/${leadId}`, data);
      loadLeads();
    } catch (error) {
      throw error;
    }
  };

  const refreshSingleLead = async (leadId) => {
    try {
      const res = await api.get(`/leads/${leadId}`);
      const fresh = res?.data?.lead || res?.data;
      if (!fresh) return;
      setLeads((prev) => prev.map((l) => (l.lead_id === leadId ? { ...l, ...fresh } : l)));
      setSelectedLead((prev) => (prev && prev.lead_id === leadId ? { ...prev, ...fresh } : prev));
    } catch (e) { console.warn('refreshSingleLead failed', leadId, e); }
  };

  const handleDeleteLead = async (leadId) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    try {
      await api.delete(`/leads/${leadId}`);
      setLeads((prev) => prev.filter((l) => l.lead_id !== leadId));
      setSelectedLead(null);
      toast.success('Lead deleted');
    } catch (error) {
      toast.error('Failed to delete lead');
    }
  };

  // ── Bulk actions ──────────────────────────────────────────────────────
  const toggleLeadSelection = (leadId) => {
    setSelectedLeads((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId); else next.add(leadId);
      return next;
    });
  };

  const filteredLeads = leads.filter((lead) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      lead.name?.toLowerCase().includes(q) ||
      lead.email?.toLowerCase().includes(q) ||
      lead.company?.toLowerCase().includes(q)
    );
  });

  const selectAllLeads = () => {
    if (selectedLeads.size === filteredLeads.length) setSelectedLeads(new Set());
    else setSelectedLeads(new Set(filteredLeads.map((l) => l.lead_id)));
  };

  const handleBulkStageChange = async (newStage) => {
    if (selectedLeads.size === 0) return;
    try {
      await Promise.all(
        Array.from(selectedLeads).map((id) => api.put(`/leads/${id}/stage`, { stage: newStage })),
      );
      setLeads((prev) => prev.map((l) => (selectedLeads.has(l.lead_id) ? { ...l, stage: newStage } : l)));
      // Refetch so the stage_change activities the backend inserts land in
      // the local cache — otherwise the audit trail looks empty until the
      // user manually reloads.
      loadLeads();
      toast.success(`${selectedLeads.size} leads moved to ${newStage} 🎯`);
      setSelectedLeads(new Set());
      setBulkActionsOpen(false);
    } catch (error) {
      toast.error('Failed to move some leads');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedLeads.size === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedLeads.size} leads? This cannot be undone.`)) return;
    try {
      await Promise.all(Array.from(selectedLeads).map((id) => api.delete(`/leads/${id}`)));
      setLeads((prev) => prev.filter((l) => !selectedLeads.has(l.lead_id)));
      toast.success(`${selectedLeads.size} leads deleted`);
      setSelectedLeads(new Set());
      setBulkActionsOpen(false);
    } catch (error) {
      toast.error('Failed to delete some leads');
    }
  };

  const handleBulkExport = () => {
    const rows = leads
      .filter((l) => selectedLeads.has(l.lead_id))
      .map((l) => [
        l.name || '', l.email || '', l.phone || '', l.company || '',
        l.stage || '', l.status || '', l.source || '',
        l.created_at ? new Date(l.created_at).toLocaleDateString() : '',
      ]);
    const headers = ['Name', 'Email', 'Phone', 'Company', 'Stage', 'Status', 'Source', 'Created'];
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${c}"`).join(',')).join('\n');
    const url = window.URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `leads_export_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success(`Exported ${selectedLeads.size} leads`);
    setSelectedLeads(new Set());
    setBulkActionsOpen(false);
  };

  const handleTemplateDownload = async () => {
    if (!selectedChatbot) { toast.error('Select an AI Employee first'); return; }
    try {
      const response = await api.get(`/leads/chatbot/${selectedChatbot}/csv-template`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'leads_import_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-full mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">🎯 Leads Pipeline</h1>
              <p className="text-sm text-slate-500">Manage and track your leads through the sales funnel</p>
            </div>
            <div className="flex items-center gap-3">
              <Select value={selectedChatbot} onValueChange={setSelectedChatbot}>
                <SelectTrigger className="w-56"><SelectValue placeholder="Select AI Employee" /></SelectTrigger>
                <SelectContent>
                  {chatbots.map((cb) => (
                    <SelectItem key={cb.chatbot_id} value={cb.chatbot_id}>🤖 {cb.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" data-testid="import-leads-btn">
                    <Upload className="w-4 h-4 mr-2" /> Import
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setShowImportModal(true)}>
                    <FileUp className="w-4 h-4 mr-2" /> Import from CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleTemplateDownload}>
                    <Download className="w-4 h-4 mr-2" /> Download Template
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Button onClick={() => setShowAddModal(true)} data-testid="add-lead-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Lead
              </Button>
            </div>
          </div>

          {pipelineStats && (
            <div className="flex items-center gap-6 mt-4 pt-4 border-t border-slate-100">
              <div className="flex items-center gap-2"><span className="text-sm text-slate-500">Total Leads:</span><span className="font-semibold text-slate-900">{pipelineStats.total_leads}</span></div>
              <div className="flex items-center gap-2"><span className="text-sm text-slate-500">Conversion Rate:</span><span className="font-semibold text-emerald-600">{pipelineStats.conversion_rate}%</span></div>
              <div className="flex items-center gap-2"><span className="text-sm text-slate-500">Won:</span><span className="font-semibold text-emerald-600">{pipelineStats.won}</span></div>
              <div className="flex items-center gap-2"><span className="text-sm text-slate-500">Lost:</span><span className="font-semibold text-red-600">{pipelineStats.lost}</span></div>
              <div className="flex-1" />
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search leads..." className="pl-9 w-64" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bulk actions bar */}
      {selectedLeads.size > 0 && (
        <div className="bg-blue-600 text-white sticky top-[73px] z-10">
          <div className="max-w-full mx-auto px-6 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Checkbox
                  checked={selectedLeads.size === filteredLeads.length}
                  onCheckedChange={selectAllLeads}
                  className="border-white data-[state=checked]:bg-white data-[state=checked]:text-blue-600"
                />
                <span className="font-medium">
                  {selectedLeads.size} lead{selectedLeads.size > 1 ? 's' : ''} selected
                </span>
              </div>

              <div className="flex items-center gap-2">
                <DropdownMenu open={bulkActionsOpen} onOpenChange={setBulkActionsOpen}>
                  <DropdownMenuTrigger asChild>
                    <Button variant="secondary" size="sm" className="bg-white/20 hover:bg-white/30 text-white border-0">
                      <MoveRight className="w-4 h-4 mr-2" /> Move to Stage
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {LEAD_STAGES.map((stage) => (
                      <DropdownMenuItem key={stage.id} onClick={() => handleBulkStageChange(stage.id)}>
                        <span className={`w-2 h-2 rounded-full ${stage.color} mr-2`} />
                        {stage.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                <Button variant="secondary" size="sm" onClick={handleBulkExport} className="bg-white/20 hover:bg-white/30 text-white border-0">
                  <Download className="w-4 h-4 mr-2" /> Export
                </Button>
                <Button variant="secondary" size="sm" onClick={handleBulkDelete} className="bg-red-500/80 hover:bg-red-500 text-white border-0">
                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setSelectedLeads(new Set())} className="text-white hover:bg-white/20">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pipeline board */}
      <div className="p-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : !selectedChatbot ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Target className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-700 mb-2">Select an AI Employee</h3>
            <p className="text-slate-500">Choose an AI agent to view its leads pipeline</p>
          </div>
        ) : (
          <div className="flex gap-4 overflow-x-auto pb-4">
            {LEAD_STAGES.filter((s) => s.id !== 'lost').map((stage) => (
              <PipelineColumn
                key={stage.id}
                stage={stage}
                leads={filteredLeads}
                onLeadClick={setSelectedLead}
                onDrop={handleDrop}
                isDraggingOver={draggingOver === stage.id}
                selectedLeads={selectedLeads}
                onToggleSelection={toggleLeadSelection}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      <LeadDetailModal
        lead={selectedLead}
        open={!!selectedLead}
        onClose={() => setSelectedLead(null)}
        onUpdate={handleUpdateLead}
        onDelete={handleDeleteLead}
        onRefresh={refreshSingleLead}
      />
      <AddLeadModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        chatbotId={selectedChatbot}
        onSuccess={loadLeads}
      />
      <CSVImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        chatbotId={selectedChatbot}
        onSuccess={loadLeads}
      />
    </div>
  );
};

export default LeadsPipelinePage;
